<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'
import { formatDisplayDateTime } from '@yuksalish/ui'
import { getProductionOrder, getProductionOrderStages, listProductionOrderOutputs, listProductionLots, deleteProductionOrder } from '../productService'
import type { ProductionLot, ProductionOrder, ProductionOrderOutputRow, ProductionOrderStage } from '../types'
import { buildHandoverText, buildProductionOrderCsv, completionPercent as calculateCompletionPercent, evaluateProductionScheduleRisk, findBottleneckStage, stageCompletionPercent, summarizeStageExceptions } from '../productionReporting'

const route = useRoute(), router = useRouter(), client = useApiClient(), id = computed(() => Number(route.params.id))
const auth = useAuthStore()
const { t } = useI18n()
const order = ref<ProductionOrder | null>(null), stages = ref<ProductionOrderStage[]>([]), lots = ref<ProductionLot[]>([])
const outputs = ref<ProductionOrderOutputRow[]>([])
const loading = ref(false), error = ref(''), handoverAt = ref(''), exportMessage = ref('')
const deactivating = ref(false)
const canManageOrder = computed(() => auth.user?.permissions.includes('production_orders.manage') ?? false)
/** Completed and already-cancelled orders have nothing to deactivate. */
const canDeactivate = computed(
  () => !!order.value && order.value.status !== 'cancelled' && order.value.status !== 'completed',
)
const produced = computed(() => Number(order.value?.produced_quantity || 0))
const planned = computed(() => Number(order.value?.planned_quantity || 0))
const remaining = computed(() => Math.max(0, planned.value - produced.value))
const completionPercent = computed(() => calculateCompletionPercent(produced.value, planned.value))
const exceptionSummary = computed(() => summarizeStageExceptions(stages.value))
const rejectedTotal = computed(() => exceptionSummary.value.rejected)
const reworkTotal = computed(() => exceptionSummary.value.rework)
const activeLots = computed(() => lots.value.filter((lot) => lot.status === 'active').length)
const completedLots = computed(() => lots.value.filter((lot) => lot.status === 'completed').length)
const bottleneck = computed(() => findBottleneckStage(stages.value))
const scheduleRisk = computed(() => order.value ? evaluateProductionScheduleRisk(order.value) : null)
const handoverSignals = computed(() => [
  ...(remaining.value > 0 ? [t('engineering.productionOrder.handoverUnitsRemaining', { count: remaining.value })] : []),
  ...(bottleneck.value ? [t('engineering.productionOrder.handoverBottleneckSignal', { stage: bottleneck.value.stage_name, percent: stagePercent(bottleneck.value) })] : []),
  ...(rejectedTotal.value > 0 ? [t('engineering.productionOrder.handoverRejectedSignal', { count: rejectedTotal.value })] : []),
  ...(reworkTotal.value > 0 ? [t('engineering.productionOrder.handoverReworkSignal', { count: reworkTotal.value })] : []),
  ...(activeLots.value > 0 ? [t('engineering.productionOrder.handoverActiveLotsSignal', { count: activeLots.value })] : []),
])

function stagePercent(stage: ProductionOrderStage) {
  return stageCompletionPercent(stage)
}

/**
 * Produced quantities per colour/size variant, grouped by stage code.
 *
 * Every recorded completion row carries its variant, so summing the rows of one
 * stage gives what that stage actually produced for each colour/size cell — which
 * the stage aggregate alone cannot show.
 */
const stageVariants = computed(() => {
  const grouped: Record<string, Array<{ colour: string; size: string; quantity: number }>> = {}
  for (const row of outputs.value) {
    const code = String(row.stage_code || '').trim()
    if (!code) continue
    const colour = String(row.color_name ?? '').trim()
    const size = String(row.size_label ?? '').trim()
    const quantity = Number(row.quantity) || 0
    const rows = (grouped[code] ??= [])
    const existing = rows.find((item) => item.colour === colour && item.size === size)
    if (existing) existing.quantity += quantity
    else rows.push({ colour, size, quantity })
  }
  for (const rows of Object.values(grouped)) {
    rows.sort((a, b) => a.colour.localeCompare(b.colour) || a.size.localeCompare(b.size))
  }
  return grouped
})

function variantsForStage(stage: ProductionOrderStage) {
  return stageVariants.value[stage.stage_code] ?? []
}

/** Quantities keep at most three decimals and never show float noise. */
function formatQuantity(value: number) {
  return Math.round((Number(value) || 0) * 1000) / 1000
}

/** Planned quantities of the order, one row per colour/size cell. */
const plannedVariants = computed(() =>
  [...(order.value?.variants ?? [])]
    .map((variant) => ({
      colour: String(variant.color_name ?? '').trim(),
      size: String(variant.size_label ?? '').trim(),
      quantity: Number(variant.planned_quantity) || 0,
    }))
    .sort(
      (a, b) =>
        a.colour.localeCompare(b.colour) ||
        a.size.localeCompare(b.size, undefined, { numeric: true }),
    ),
)
const plannedVariantTotal = computed(() =>
  plannedVariants.value.reduce((sum, variant) => sum + variant.quantity, 0),
)
function printHandover() {
  handoverAt.value = formatDisplayDateTime(new Date())
  window.print()
}
function safeFileName(extension: string) {
  return `${order.value?.mo_code || `production-order-${id.value}`}-handover.${extension}`.replace(/[^a-z0-9._-]+/gi, '-')
}
function downloadFile(content: string, type: string, fileName: string) {
  const url = URL.createObjectURL(new Blob([content], { type }))
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  anchor.click()
  URL.revokeObjectURL(url)
}
function refreshSnapshot() {
  const snapshot = new Date().toISOString()
  handoverAt.value = formatDisplayDateTime(snapshot)
  return snapshot
}
function exportCsv() {
  if (!order.value) return
  downloadFile(
    buildProductionOrderCsv(order.value, stages.value, lots.value, refreshSnapshot()),
    'text/csv;charset=utf-8',
    safeFileName('csv'),
  )
  exportMessage.value = t('engineering.productionOrder.handoverExported')
}
async function shareHandover() {
  if (!order.value) return
  const text = buildHandoverText(order.value, stages.value, lots.value, refreshSnapshot())
  const file = new File([text], safeFileName('txt'), { type: 'text/plain' })
  const shareData: ShareData = { title: `${t('engineering.productionOrder.orderEyebrow')} ${order.value.mo_code}`, text, files: [file] }
  try {
    if (navigator.share && (!navigator.canShare || navigator.canShare(shareData))) {
      await navigator.share(shareData)
      exportMessage.value = t('engineering.productionOrder.handoverShared')
      return
    }
  } catch (cause) {
    if ((cause as DOMException)?.name === 'AbortError') return
  }
  downloadFile(text, 'text/plain;charset=utf-8', file.name)
  exportMessage.value = t('engineering.productionOrder.handoverDownloaded')
}
async function load() {
  loading.value = true
  error.value = ''
  try {
    [order.value, stages.value, lots.value, outputs.value] = await Promise.all([
      getProductionOrder(client, id.value),
      getProductionOrderStages(client, id.value),
      listProductionLots(client, id.value),
      // The order page must still render when the output ledger is unreachable.
      listProductionOrderOutputs(client, id.value).catch(() => []),
    ])
    handoverAt.value = formatDisplayDateTime(new Date())
  } catch (cause) { error.value = formatApiError(cause) }
  finally { loading.value = false }
}
onMounted(load)

/** Deactivate = DELETE the order (releases reservations, soft-deletes it). */
async function deactivateOrder() {
  if (!order.value || deactivating.value) return
  if (!window.confirm(t('engineering.productionOrder.confirmDeactivate', { code: order.value.mo_code }))) return
  deactivating.value = true
  try {
    await deleteProductionOrder(client, order.value.id)
    // The order is soft-deleted: its detail route no longer resolves.
    await router.push('/production-orders')
  } catch (cause) {
    exportMessage.value = formatApiError(cause)
  } finally {
    deactivating.value = false
  }
}
</script>

<template>
  <div class="page-toolbar">
    <RouterLink class="back" :data-tooltip="t('engineering.productionOrder.backToOrders')" to="/production-orders">{{ t('engineering.productionOrder.backToOrders') }}</RouterLink>
    <div v-if="order" class="page-toolbar__actions actions">
      <button class="action secondary" type="button" @click="exportCsv">{{ t('engineering.productionOrder.exportCsv') }}</button>
      <button class="action secondary" type="button" @click="shareHandover">{{ t('engineering.productionOrder.shareHandover') }}</button>
      <button
        v-if="canManageOrder && canDeactivate"
        class="action action--danger"
        type="button"
        :disabled="deactivating"
        @click="deactivateOrder"
      >
        {{ deactivating ? t('engineering.productionOrder.deactivatingOrder') : t('engineering.productionOrder.deactivateOrder') }}
      </button>
      <button class="print" type="button" @click="printHandover">{{ t('engineering.productionOrder.printHandover') }}</button>
      <RouterLink class="action" :to="`/production-orders/${order.id}/stages`">{{ t('engineering.productionOrder.openStageBoard') }}</RouterLink>
      <RouterLink class="action primary" :to="`/production-orders/${order.id}/lots`">{{ t('engineering.productionOrder.openLotQueue') }}</RouterLink>
    </div>
  </div>
  <p v-if="exportMessage" class="export-message" role="status">{{ exportMessage }}</p>
  <div v-if="loading" class="state">{{ t('engineering.productionOrder.loadingOrder') }}</div>
  <div v-else-if="error" class="state error">{{ error }}</div>
  <template v-else-if="order">
    <header>
      <div><p class="eyebrow">{{ t('engineering.productionOrder.orderEyebrow') }}</p><h2>{{ order.mo_code }}</h2><p>{{ order.product_name || t('engineering.productionOrder.productNumber', { id: order.product_id }) }}</p></div>
    </header>
    <div v-if="scheduleRisk" class="schedule-alert" :class="scheduleRisk.level" role="alert"><strong>{{ t('engineering.productionOrder.scheduleAttention') }}</strong> {{ scheduleRisk.label }}. {{ t('engineering.productionOrder.handoverUnitsRemaining', { count: remaining }) }}</div>
    <section class="summary">
      <div><span>{{ t('engineering.productionOrder.status') }}</span><strong>{{ t(`engineering.state.${order.status}`) }}</strong></div>
      <div><span>{{ t('engineering.productionOrder.plan') }}</span><strong>{{ planned }}</strong></div>
      <div><span>{{ t('engineering.productionOrder.actual') }}</span><strong>{{ produced }}</strong></div>
      <div :class="{ warning: remaining > 0 }"><span>{{ t('engineering.productionOrder.remaining') }}</span><strong>{{ remaining }}</strong></div>
      <div><span>{{ t('engineering.productionOrder.completion') }}</span><strong>{{ completionPercent }}%</strong></div>
      <div><span>{{ t('engineering.productionOrder.activeLots') }}</span><strong>{{ activeLots }}</strong></div>
      <div :class="{ exception: rejectedTotal > 0 }"><span>{{ t('engineering.productionOrder.rejected') }}</span><strong>{{ rejectedTotal }}</strong></div>
      <div :class="{ warning: reworkTotal > 0 }"><span>{{ t('engineering.productionOrder.rework') }}</span><strong>{{ reworkTotal }}</strong></div>
    </section>
    <section class="handover">
      <div class="section-heading">
        <div><p class="eyebrow">{{ t('engineering.productionOrder.shiftHandover') }}</p><h3>{{ t('engineering.productionOrder.supervisorAttention') }}</h3></div>
        <small>{{ t('engineering.productionOrder.snapshot') }}: {{ handoverAt }}</small>
      </div>
      <p v-if="!handoverSignals.length" class="clear">{{ t('engineering.productionOrder.noExceptions') }}</p>
      <ul v-else><li v-for="signal in handoverSignals" :key="signal">{{ signal }}</li></ul>
      <div class="lot-stats"><span>{{ t('engineering.productionOrder.activeLotsCount', { count: activeLots }) }}</span><span>{{ t('engineering.productionOrder.completedLotsCount', { count: completedLots }) }}</span><span>{{ t('engineering.productionOrder.totalLotsCount', { count: lots.length }) }}</span></div>
    </section>
    <section class="planned-variants-card">
      <div class="section-heading">
        <div>
          <p class="eyebrow">{{ t('engineering.productionOrder.plannedVariantsEyebrow') }}</p>
          <h3>{{ t('engineering.productionOrder.plannedVariantsTitle') }}</h3>
        </div>
        <span class="bottleneck">{{ t('engineering.productionOrder.plannedVariantsTotal', { count: formatQuantity(plannedVariantTotal) }) }}</span>
      </div>
      <p v-if="!plannedVariants.length" class="variant-empty">{{ t('engineering.productionOrder.plannedVariantsEmpty') }}</p>
      <table v-else class="planned-variants">
        <thead>
          <tr>
            <th>{{ t('engineering.productionOrder.stageVariantColour') }}</th>
            <th>{{ t('engineering.productionOrder.stageVariantSize') }}</th>
            <th class="num">{{ t('engineering.productionOrder.plan') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="variant in plannedVariants" :key="`${variant.colour}-${variant.size}`">
            <td>{{ variant.colour || '—' }}</td>
            <td>{{ variant.size || '—' }}</td>
            <td class="num">{{ formatQuantity(variant.quantity) }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td colspan="2">{{ t('engineering.productionOrder.plannedVariantsTotalLabel') }}</td>
            <td class="num">{{ formatQuantity(plannedVariantTotal) }}</td>
          </tr>
        </tfoot>
      </table>
    </section>
    <section>
      <div class="section-heading"><h3>{{ t('engineering.productionOrder.stagePlanVsActual') }}</h3><span v-if="bottleneck" class="bottleneck">{{ t('engineering.productionOrder.bottleneck', { name: bottleneck.stage_name }) }}</span></div>
      <div v-if="!stages.length" class="empty">{{ t('engineering.productionOrder.noBomStages') }}</div>
      <div v-else class="stages">
        <article v-for="stage in stages" :key="stage.id" :class="{ bottleneck_stage: bottleneck?.id === stage.id }">
          <div class="stage-title"><span class="sequence">{{ stage.operation_sequence }}</span><div><strong>{{ stage.stage_name }}</strong><small>{{ stage.stage_code }}</small></div><span class="badge">{{ t(`engineering.state.${stage.status}`) }}</span></div>
          <div class="stage-metrics"><span>{{ t('engineering.productionOrder.plan') }} <strong>{{ stage.planned_quantity }}</strong></span><span>{{ t('engineering.productionOrder.actual') }} <strong>{{ stage.completed_quantity }}</strong></span><span>{{ t('engineering.productionOrder.variance') }} <strong>{{ Math.max(0, stage.planned_quantity - stage.completed_quantity) }}</strong></span><span>{{ t('engineering.productionOrder.yield') }} <strong>{{ stagePercent(stage) }}%</strong></span></div>
          <progress :value="stage.completed_quantity" :max="stage.planned_quantity"></progress>
          <p>{{ stage.assigned_employee_name ? t('engineering.productionOrder.stageRejectedReworkEmployee', { rejected: stage.rejected_quantity, rework: stage.rework_quantity, employee: stage.assigned_employee_name }) : t('engineering.productionOrder.stageRejectedRework', { rejected: stage.rejected_quantity, rework: stage.rework_quantity }) }}</p>
          <!-- Produced quantities of this stage per colour/size variant (accordion). -->
          <details v-if="variantsForStage(stage).length" class="stage-variants">
            <summary>
              <span>{{ t('engineering.productionOrder.stageVariantTitle') }}</span>
              <small>{{ t('engineering.productionOrder.stageVariantCount', { count: variantsForStage(stage).length }) }}</small>
            </summary>
            <table>
              <thead>
                <tr>
                  <th>{{ t('engineering.productionOrder.stageVariantColour') }}</th>
                  <th>{{ t('engineering.productionOrder.stageVariantSize') }}</th>
                  <th class="num">{{ t('engineering.productionOrder.stageVariantQuantity') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="variant in variantsForStage(stage)" :key="`${variant.colour}-${variant.size}`">
                  <td>{{ variant.colour || '—' }}</td>
                  <td>{{ variant.size || '—' }}</td>
                  <td class="num">{{ formatQuantity(variant.quantity) }}</td>
                </tr>
              </tbody>
            </table>
          </details>
          <p v-else class="variant-empty">{{ t('engineering.productionOrder.stageVariantEmpty') }}</p>
        </article>
      </div>
    </section>
  </template>
</template>

<style scoped>
.back{display:inline-block;margin-bottom:20px;color:#285b9a;font-weight:700}header{display:flex;align-items:center;justify-content:space-between;gap:20px}.eyebrow{margin:0;color:#66726e;font-size:12px;font-weight:800;text-transform:uppercase}h2{margin:4px 0}.actions{display:flex;gap:8px}.action,.print{padding:10px 14px;border:0;color:#fff;background:#3568a8;font-weight:700;text-decoration:none;cursor:pointer}.action.secondary{color:#285b9a;background:#eaf1f8}.primary,.print{background:#07996f}.export-message{margin:0 0 12px;padding:9px 12px;color:#236451;background:#eaf7f2;font-weight:700}.schedule-alert{margin:14px 0 0;padding:12px 14px;border-left:4px solid}.schedule-alert.warning{border-color:#d49a22;color:#765213;background:#fff3c9}.schedule-alert.critical{border-color:#c64a39;color:#852d22;background:#ffe1dc}.summary{display:grid;grid-template-columns:repeat(auto-fit,minmax(118px,1fr));gap:8px;margin:14px 0}.summary div,section{padding:18px;border:1px solid #d7dfdc;background:#fff}.summary div{display:grid;gap:2px;padding:10px 12px}.summary span,small{color:#66726e}.summary span{font-size:10px;font-weight:800;letter-spacing:.03em;text-transform:uppercase;line-height:1.2}.summary strong{font-size:18px;line-height:1.2;font-variant-numeric:tabular-nums}.summary .exception{border-color:#efb5ad;background:#fff3f1}.summary .warning{border-color:#ecd28a;background:#fffae9}.section-heading{display:flex;align-items:center;justify-content:space-between;gap:12px}.section-heading h3{margin:0}.handover{margin-bottom:16px}.handover ul{display:grid;gap:7px;margin:14px 0;padding-left:20px}.handover li::marker{color:#c16b1b}.clear{color:#236451;font-weight:700}.lot-stats{display:flex;gap:8px;flex-wrap:wrap}.lot-stats span,.bottleneck{padding:6px 9px;color:#6b4b12;background:#fffae9;font-size:12px;font-weight:800}.stages{display:grid;gap:9px;margin-top:14px}.stages article{padding:12px;border:1px solid #e1e7e5;background:#f8faf9}.stages article.bottleneck_stage{border-color:#e2b65d;background:#fffae9}.stage-title{display:flex;align-items:center;gap:9px}.stage-title>div{display:grid}.sequence{display:grid;width:28px;height:28px;color:#fff;background:#3568a8;place-items:center}.badge{margin-left:auto;padding:5px 8px;color:#236451;background:#eaf7f2;text-transform:capitalize}.stage-metrics{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-top:10px}.stage-metrics span{display:grid;color:#66726e;font-size:12px}.stage-metrics strong{color:#14243a;font-size:16px}.stages progress{width:100%;margin-top:10px}.stages p{margin:5px 0 0;color:#53625d;font-size:13px}.state{display:grid;min-height:220px;place-items:center}.error{color:#852d22}@media(max-width:800px){header,.section-heading{align-items:stretch;flex-direction:column}.actions{flex-wrap:wrap}.summary{grid-template-columns:repeat(2,1fr)}}@media(max-width:520px){.actions{display:grid}.stage-metrics{grid-template-columns:repeat(2,1fr)}}@media print{.back,.actions,.export-message{display:none!important}header{margin-bottom:14px}section{break-inside:avoid}.handover{border:2px solid #14243a}}
</style>

<style scoped>
/* Produced quantities per colour/size variant, under each stage (accordion). */
.stage-variants{margin-top:10px;border-top:1px solid #e1e7e5}
.stage-variants>summary{display:flex;align-items:center;gap:8px;padding:9px 0 3px;color:#53625d;font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.03em;cursor:pointer;list-style:none}
.stage-variants>summary::-webkit-details-marker{display:none}
.stage-variants>summary::after{content:'';width:7px;height:7px;margin-left:auto;border-right:2px solid #66726e;border-bottom:2px solid #66726e;transform:rotate(45deg);transition:transform .15s}
.stage-variants[open]>summary::after{transform:rotate(-135deg)}
.stage-variants>summary:hover{color:#2d5e96}
.stage-variants>summary small{color:#8a958f;font-size:11px;font-weight:700;letter-spacing:0;text-transform:none}
.stage-variants table{width:100%;max-width:420px;margin-top:4px;border-collapse:collapse;font-size:13px}
.stage-variants th,.stage-variants td{padding:4px 8px;border-bottom:1px solid #e1e7e5;text-align:left}
.stage-variants tbody tr:last-child td{border-bottom:0}
.stage-variants thead th{color:#66726e;font-size:11px;font-weight:800;text-transform:uppercase}
.stage-variants td.num{text-align:right;color:#14243a;font-weight:700;font-variant-numeric:tabular-nums}
.variant-empty{margin:8px 0 0;color:#8a958f;font-size:12px}
/* Planned colour/size quantities card. */
.planned-variants-card{margin-bottom:16px}
/* Deactivate action: outlined red, so it never reads as a primary action. */
.action.action--danger{border:1px solid #d7a8a1;color:#8b3025;background:#fff5f3}
.action.action--danger:hover:not(:disabled){background:#ffe1dc}
.action.action--danger:disabled{opacity:.55;cursor:not-allowed}
.planned-variants{width:100%;max-width:560px;margin-top:12px;border-collapse:collapse;font-size:13px}
.planned-variants th,.planned-variants td{padding:5px 10px;border-bottom:1px solid #e1e7e5;text-align:left}
.planned-variants thead th{color:#66726e;font-size:11px;font-weight:800;text-transform:uppercase}
.planned-variants td.num,.planned-variants th.num{text-align:right;font-weight:700;font-variant-numeric:tabular-nums}
.planned-variants tfoot td{border-bottom:0;color:#14243a;background:#f7faf9;font-weight:800}
</style>

